import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tab,
  Tabs,
} from '@mui/material';
import { Box, Typography } from '@weave-mui/material';
import MaterialTable from 'material-table';
import Select, { components as selectComponents } from 'react-select';
import CollapsibleContent from 'components/CollapsibleContent';
import IconButton from '@material-ui/core/IconButton';
import ReactTooltip from 'react-tooltip';
import ManageTestDataEmptyState from './ManageTestDataEmptyState';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  ViewColumn as ViewColumnIcon,
  AddBox as AddBoxIcon,
  Undo as UndoIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
} from '@material-ui/icons';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { debounce } from 'lodash';

import BulkAddFieldSection from 'components/BulkAddFieldSection';
import { addNewNotification } from 'store/actions/notification_actions';
import downloadFile from 'utils/downloadFile';
import { BUILD_TOOLS } from 'utils/consts';

// Generic input component for all text fields with cursor position tracking
const DebouncedInputField = ({
  elementId,
  env,
  rowIndex,
  fieldKey,
  initialValue,
  onUpdate,
  validationErrors,
  envKey,
  className = 'form-control',
  showValidation = false,
  inputStyle,
}) => {
  const inputRef = useRef(null);
  const [tempValue, setTempValue] = useState(initialValue);
  const [cursorPosition, setCursorPosition] = useState(null);

  // Debounced update
  const debouncedUpdate = useRef(
    debounce((value) => {
      onUpdate(value);
    }, 400),
  ).current;

  // Update temp value when initial value changes
  useEffect(() => {
    setTempValue(initialValue);
  }, [initialValue]);

  // Restore cursor position after re-render
  useEffect(() => {
    if (inputRef.current && cursorPosition !== null) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setCursorPosition(null);
    }
  });

  const handleChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;

    setCursorPosition(cursor);
    setTempValue(value);
    debouncedUpdate(value);
  };

  const handleBlur = (e) => {
    debouncedUpdate.cancel();
    onUpdate(e.target.value);
  };

  const rowErrors = validationErrors[elementId]?.[envKey]?.[rowIndex];
  const cellInvalidValue = rowErrors?.invalidValueByColumn?.[fieldKey];
  const nameFieldError =
    fieldKey === '_name' &&
    (rowErrors?.requiredFields || rowErrors?.uniqueName)
      ? rowErrors.requiredFields || rowErrors.uniqueName
      : null;
  const hasValidationError =
    showValidation &&
    rowErrors &&
    (nameFieldError || cellInvalidValue);

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        value={tempValue}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`${className} ${hasValidationError ? 'is-invalid' : ''}`}
        title={tempValue}
        style={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          ...inputStyle,
        }}
      />
      {hasValidationError && (
        <div className="invalid-feedback">
          {nameFieldError}
          {cellInvalidValue}
        </div>
      )}
    </div>
  );
};

/**
 * ManageTestDataEditor
 *
 * Reusable editor body for test data management. This component is intentionally
 * UI-only (no modal chrome) so it can be embedded in both a modal and a full page.
 *
 * The parent can trigger save via `ref.current.submit()` and can listen to
 * `onStateChange` for `submitEnabled` / `loading` state.
 *
 * @param {Object} props
 * @param {Array<Object>} props.elements - Selected test cases (already transformed for table view)
 * @param {Function} props.onSubmit - Submit handler (payload, uploadedFiles) => Promise
 * @param {string} props.title - Title (used for editor context; page/modal should render the visible title)
 * @param {Object} props.parent - Active project (must include tools + envlist)
 * @param {Function} [props.onStateChange] - Callback invoked with {submitEnabled, loading, showActions}
 * @param {React.Ref} ref - Imperative handle with submit()
 * @returns {JSX.Element}
 */
const ManageTestDataEditor = forwardRef(
  ({ elements, onSubmit, title, parent, onStateChange = () => {} }, ref) => {
    const { tools: buildTool, envlist } = parent || {};

    const MAX_COLUMNS = 20;
    const MAX_ROWS = 50;
    const MAX_FILENAME_LENGTH = 15;

    const [iterationFiles, setIterationFiles] = useState(() => {
      const files = {};
      (elements || []).forEach((testCase) => {
        const testCaseId = testCase.id;
        files[testCaseId] = {};
        Object.keys(testCase)
          .filter((key) => key.startsWith('testdata_'))
          .forEach((envKey) => {
            const iterationFile = (testCase[envKey] || []).find(
              (row) => row.testiterationfile,
            );
            if (iterationFile) {
              files[testCaseId][envKey] = iterationFile.testiterationfile;
            }
          });
      });
      return files;
    });

    const [testDataState, setTestDataState] = useState(() => {
      return (elements || []).map((testCase) => {
        const updatedTestCase = { ...testCase };
        const allEnvs = [
          ...new Set([
            ...Object.keys(testCase),
            ...(envlist || []).map(
              ({ env }) => `testdata_${env.toLowerCase()}`,
            ),
          ]),
        ];
        allEnvs
          .filter((key) => key.startsWith('testdata_'))
          .forEach((envKey) => {
            if (!updatedTestCase[envKey]) {
              updatedTestCase[envKey] = [{ _tags: '', _name: '' }];
              updatedTestCase.columnsOrder = {
                ...updatedTestCase.columnsOrder,
                [envKey]: [],
              };
            } else {
              updatedTestCase[envKey] = updatedTestCase[envKey].map((row) => {
                if (row.testiterationfile) {
                  const { testiterationfile, ...rest } = row;
                  return rest;
                }
                return row;
              });
            }
          });
        return updatedTestCase;
      });
    });

    const [runAsSingleTransaction, setRunAsSingleTransaction] = useState(() => {
      const initialRunAsSingleTransaction = {};
      (elements || []).forEach((testCase) => {
        const testCaseId = testCase.id;
        initialRunAsSingleTransaction[testCaseId] = {};
        Object.keys(testCase)
          .filter((key) => key.startsWith('run_as_single_transaction_'))
          .forEach((runAsSingleTransactionEnv) => {
            const envKey = runAsSingleTransactionEnv
              .replace('run_as_single_transaction_', '')
              .toUpperCase();
            initialRunAsSingleTransaction[testCaseId][envKey] =
              testCase[runAsSingleTransactionEnv];
          });
      });
      return initialRunAsSingleTransaction;
    });

    const [columnBeingEdited, setColumnBeingEdited] = useState({
      key: '',
      elementId: '',
    });
    const [inputValue, setInputValue] = useState('');
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const inputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [previousColumnTitle, setPreviousColumnTitle] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [columnTitleErrors, setColumnTitleErrors] = useState({});
    const compactTable = true;

    const globalFields = useSelector(
      (state) => state.hosting.globalFields.fields,
    );
    const dispatch = useDispatch();
    const globalFieldsKeys = (globalFields || []).map((field) => field.key);

    // Extract environment keys
    const envKeys = [
      ...new Set([
        ...Object.keys((elements && elements[0]) || {})
          .filter((key) => key.startsWith('testdata_'))
          .map((key) => key.replace('testdata_', '').toUpperCase()),
        ...(envlist || []).map(({ env }) => env),
      ]),
    ];

    const [currentEnv, setCurrentEnv] = useState(envKeys[0]);

    // Open/close menu when entering/exiting edit mode for column title
    useEffect(() => {
      if (columnBeingEdited.key && columnBeingEdited.elementId) {
        setMenuIsOpen(true);
      } else {
        setMenuIsOpen(false);
      }
    }, [columnBeingEdited]);

    // Select all text when menu opens
    useEffect(() => {
      if (
        menuIsOpen &&
        columnBeingEdited.key &&
        columnBeingEdited.elementId &&
        inputRef.current &&
        inputRef.current.inputRef
      ) {
        const timer = setTimeout(() => {
          const inputElement = inputRef.current.inputRef;
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 0);
        return () => clearTimeout(timer);
      }
    }, [menuIsOpen, columnBeingEdited]);

    useEffect(() => {
      const newRunAsSingleTransaction = { ...runAsSingleTransaction };

      testDataState.forEach((testCase) => {
        const testCaseId = testCase.id;
        const envKey = `testdata_${currentEnv.toLowerCase()}`;
        const runAsSingleTransactionKey = `run_as_single_transaction_${currentEnv.toLowerCase()}`;

        if (testCase[runAsSingleTransactionKey] !== undefined) {
          newRunAsSingleTransaction[testCaseId] =
            newRunAsSingleTransaction[testCaseId] || {};
          newRunAsSingleTransaction[testCaseId][currentEnv] =
            testCase[runAsSingleTransactionKey];
        } else {
          const hasFile = testCase[envKey].some((row) => row.testdatafile);
          newRunAsSingleTransaction[testCaseId] =
            newRunAsSingleTransaction[testCaseId] || {};
          newRunAsSingleTransaction[testCaseId][currentEnv] = !hasFile;
        }
      });

      setRunAsSingleTransaction(newRunAsSingleTransaction);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEnv, testDataState]);

    const hasOnlyNameAndTags = (element, envKey) =>
      Object.keys(element[envKey][0]).every(
        (key) => key === '_name' || key === '_tags',
      );

    const validateRequiredFields = () => {
      const errors = {};
      const valueRegex = /^[a-zA-Z0-9/@., _;:*()='"?&!<>+%[\]{}$#|-]+$/;
      const excludedValueKeys = [
        '_name',
        '_tags',
        'testdatafile',
        'testiterationfile',
      ];

      testDataState.forEach((testCase) => {
        const testCaseId = testCase.id;
        Object.keys(testCase)
          .filter((key) => key.startsWith('testdata_'))
          .forEach((envKey) => {
            if (hasOnlyNameAndTags(testCase, envKey)) {
              return;
            }
            const columnKeys =
              (testCase.columnsOrder && testCase.columnsOrder[envKey]) || [];
            const dataColumnKeys = columnKeys.filter(
              (colKey) => !excludedValueKeys.includes(colKey),
            );

            testCase[envKey].forEach((row, rowIndex) => {
              if (!row._name || row._name.trim() === '') {
                errors[testCaseId] = errors[testCaseId] || {};
                errors[testCaseId][envKey] = errors[testCaseId][envKey] || {};
                errors[testCaseId][envKey][rowIndex] =
                  errors[testCaseId][envKey][rowIndex] || {};
                errors[testCaseId][envKey][rowIndex].requiredFields =
                  'Required field is missing';
              }

              dataColumnKeys.forEach((colKey) => {
                const cellValue = row[colKey];
                const isInvalid =
                  cellValue != null &&
                  typeof cellValue === 'string' &&
                  cellValue.trim() !== '' &&
                  !valueRegex.test(cellValue);
                if (isInvalid) {
                  errors[testCaseId] = errors[testCaseId] || {};
                  errors[testCaseId][envKey] = errors[testCaseId][envKey] || {};
                  errors[testCaseId][envKey][rowIndex] =
                    errors[testCaseId][envKey][rowIndex] || {};
                  errors[testCaseId][envKey][rowIndex].invalidValueByColumn =
                    errors[testCaseId][envKey][rowIndex].invalidValueByColumn ||
                    {};
                  errors[testCaseId][envKey][rowIndex].invalidValueByColumn[
                    colKey
                  ] = 'Invalid character(s) found';
                }
              });
            });
          });
      });
      setValidationErrors(errors);
    };

    useEffect(() => {
      validateRequiredFields();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Validate bulk field key for adding to multiple jobs
     * @param {string} fieldKey - The field key to validate
     * @param {string} currentEnvironment - The current environment
     * @returns {Object} - {isValid: boolean, errorMessage: string}
     */
    const validateBulkFieldKey = (fieldKey, currentEnvironment) => {
      if (!fieldKey || fieldKey.trim() === '') {
        return {
          isValid: false,
          errorMessage: 'Field key cannot be empty',
        };
      }

      // ReDoS protection: Limit input length before regex
      if (fieldKey.length > 100) {
        return {
          isValid: false,
          errorMessage: 'Field key is too long (max 100 characters)',
        };
      }

      const fieldKeyRegex = /^[a-zA-Z0-9_-]+$/;
      if (!fieldKeyRegex.test(fieldKey)) {
        return {
          isValid: false,
          errorMessage: 'Invalid characters in key',
        };
      }

      if (/^\d/.test(fieldKey)) {
        return {
          isValid: false,
          errorMessage: 'Key cannot start with a number',
        };
      }

      const reservedFields = ['_name', '_tags', 'testdatafile'];
      if (reservedFields.includes(fieldKey)) {
        return {
          isValid: false,
          errorMessage: `Field '${fieldKey}' is a reserved field name`,
        };
      }

      return {
        isValid: true,
        errorMessage: '',
      };
    };

    /**
     * Validate bulk field value for adding to multiple jobs
     * @param {string} fieldValue - The field value to validate
     * @returns {Object} - {isValid: boolean, errorMessage: string}
     */
    const validateBulkFieldValue = (fieldValue) => {
      if (!fieldValue || fieldValue.trim() === '') {
        return {
          isValid: true,
          errorMessage: '',
        };
      }

      const fieldValueRegex = /^[a-zA-Z0-9/@., _;:*()='"?&!<>+%[\]{}$#|-]+$/;
      if (!fieldValueRegex.test(fieldValue)) {
        return {
          isValid: false,
          errorMessage: 'Invalid character(s) found',
        };
      }

      return {
        isValid: true,
        errorMessage: '',
      };
    };

    const handleRunAsSingleTransactionChange = (testCaseId, env, value) => {
      const newRunAsSingleTransaction = { ...runAsSingleTransaction };
      newRunAsSingleTransaction[testCaseId] =
        newRunAsSingleTransaction[testCaseId] || {};
      newRunAsSingleTransaction[testCaseId][env] = value;
      setRunAsSingleTransaction((prevState) => ({
        ...prevState,
        ...newRunAsSingleTransaction,
      }));
    };

    // remove tableData property
    const removeTableData = (data) => {
      return data.map(({ tableData, ...rest }) => rest);
    };

    const debouncedValidateFields = debounce(
      (elementId, envKey, updatedData) => {
        validateFields(elementId, envKey, updatedData);
      },
      300,
    );

    // Handle input change
    const handleInputChange = (elementId, env, key, value, rowIndex) => {
      let updatedStateForValidation = null;
      setTestDataState((prevState) => {
        const elementIndex = prevState.findIndex(
          (item) => item.id === elementId,
        );
        const envKey = `testdata_${env.toLowerCase()}`;

        if (elementIndex !== -1) {
          const updatedState = [...prevState];
          const updatedElement = { ...updatedState[elementIndex] };
          const updatedEnvData = [...updatedElement[envKey]];

          updatedEnvData[rowIndex] = {
            ...updatedEnvData[rowIndex],
            [key]: value,
          };

          updatedElement[envKey] = updatedEnvData;
          updatedState[elementIndex] = updatedElement;
          updatedStateForValidation = updatedState;

          return updatedState;
        }

        return prevState;
      });

      if (updatedStateForValidation) {
        const envKey = `testdata_${env.toLowerCase()}`;
        debouncedValidateFields(elementId, envKey, updatedStateForValidation);
      }
    };

    const validateFields = (elementId, envKey, updatedData) => {
      const errors = { ...validationErrors };
      const testCase = updatedData.find((item) => item.id === elementId);
      const names = testCase[envKey].map((row) => row._name.toLowerCase());
      const duplicateNames = names.filter(
        (name, index) => names.indexOf(name) !== index,
      );

      testCase[envKey].forEach((row, rowIndex) => {
        if (duplicateNames.includes(row._name.toLowerCase())) {
          errors[elementId] = errors[elementId] || {};
          errors[elementId][envKey] = errors[elementId][envKey] || {};
          errors[elementId][envKey][rowIndex] =
            errors[elementId][envKey][rowIndex] || {};
          errors[elementId][envKey][rowIndex].uniqueName =
            'Duplicate names found';
        } else if (
          errors[elementId] &&
          errors[elementId][envKey] &&
          errors[elementId][envKey][rowIndex]
        ) {
          delete errors[elementId][envKey][rowIndex].uniqueName;
          if (Object.keys(errors[elementId][envKey][rowIndex]).length === 0) {
            delete errors[elementId][envKey][rowIndex];
          }
          if (Object.keys(errors[elementId][envKey]).length === 0) {
            delete errors[elementId][envKey];
          }
          if (Object.keys(errors[elementId]).length === 0) {
            delete errors[elementId];
          }
        }
      });

      const requiredFields = ['_name'];
      testCase[envKey].forEach((row, rowIndex) => {
        const missingFields = requiredFields.filter(
          (field) => !row[field] || row[field].trim() === '',
        );

        if (missingFields.length > 0) {
          errors[elementId] = errors[elementId] || {};
          errors[elementId][envKey] = errors[elementId][envKey] || {};
          errors[elementId][envKey][rowIndex] =
            errors[elementId][envKey][rowIndex] || {};
          errors[elementId][envKey][rowIndex].requiredFields =
            'Required field is missing';
        } else if (
          errors[elementId] &&
          errors[elementId][envKey] &&
          errors[elementId][envKey][rowIndex]
        ) {
          delete errors[elementId][envKey][rowIndex].requiredFields;
          if (Object.keys(errors[elementId][envKey][rowIndex]).length === 0) {
            delete errors[elementId][envKey][rowIndex];
          }
          if (Object.keys(errors[elementId][envKey]).length === 0) {
            delete errors[elementId][envKey];
          }
          if (Object.keys(errors[elementId]).length === 0) {
            delete errors[elementId];
          }
        }
      });

      const excludedValueKeys = [
        '_name',
        '_tags',
        'testdatafile',
        'testiterationfile',
      ];
      const valueRegex = /^[a-zA-Z0-9/@., _;:*()='"?&!<>+%[\]{}$#|-]+$/;
      const columnKeys =
        (testCase.columnsOrder && testCase.columnsOrder[envKey]) || [];
      const dataColumnKeys = columnKeys.filter(
        (colKey) => !excludedValueKeys.includes(colKey),
      );

      testCase[envKey].forEach((row, rowIndex) => {
        dataColumnKeys.forEach((colKey) => {
          const cellValue = row[colKey];
          const isInvalid =
            cellValue != null &&
            typeof cellValue === 'string' &&
            cellValue.trim() !== '' &&
            !valueRegex.test(cellValue);

          if (isInvalid) {
            errors[elementId] = errors[elementId] || {};
            errors[elementId][envKey] = errors[elementId][envKey] || {};
            errors[elementId][envKey][rowIndex] =
              errors[elementId][envKey][rowIndex] || {};
            errors[elementId][envKey][rowIndex].invalidValueByColumn =
              errors[elementId][envKey][rowIndex].invalidValueByColumn || {};
            errors[elementId][envKey][rowIndex].invalidValueByColumn[colKey] =
              'Invalid character(s) found';
          } else if (
            errors[elementId]?.[envKey]?.[rowIndex]?.invalidValueByColumn?.[
              colKey
            ]
          ) {
            delete errors[elementId][envKey][rowIndex].invalidValueByColumn[
              colKey
            ];
            if (
              Object.keys(
                errors[elementId][envKey][rowIndex].invalidValueByColumn,
              ).length === 0
            ) {
              delete errors[elementId][envKey][rowIndex].invalidValueByColumn;
            }
            if (Object.keys(errors[elementId][envKey][rowIndex]).length === 0) {
              delete errors[elementId][envKey][rowIndex];
            }
            if (Object.keys(errors[elementId][envKey]).length === 0) {
              delete errors[elementId][envKey];
            }
            if (Object.keys(errors[elementId]).length === 0) {
              delete errors[elementId];
            }
          }
        });
      });

      if (hasOnlyNameAndTags(testCase, envKey)) {
        delete errors[elementId];
      }

      if (errors[elementId]) {
        if (
          errors[elementId][envKey] &&
          Object.keys(errors[elementId][envKey]).length === 0
        ) {
          delete errors[elementId][envKey];
        }
        if (Object.keys(errors[elementId]).length === 0) {
          delete errors[elementId];
        }
      }

      setValidationErrors(errors);
    };

    const handleAddRow = (elementId, env, rowData) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const updatedData = testDataState.map((item) => {
        if (item.id === elementId) {
          const newRow = { ...rowData, testdatafile: '', _name: '' };
          delete newRow.tableData;
          return {
            ...item,
            [envKey]: [...item[envKey], newRow],
          };
        }
        return item;
      });
      setTestDataState(updatedData);
      validateFields(elementId, envKey, updatedData);
    };

    const handleFileDelete = async (testCaseId, env, rowIndex) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const updatedData = testDataState.map((item) => {
        if (item.id === testCaseId) {
          const newData = [...item[envKey]];
          newData[rowIndex].testdatafile = '';
          return { ...item, [envKey]: newData };
        }
        return item;
      });
      setTestDataState(updatedData);
    };

    const handleRemoveRow = async (elementId, env, rowIndex) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const fileUrl = testDataState.find((item) => item.id === elementId)[
        envKey
      ][rowIndex].testdatafile;

      if (fileUrl) {
        await handleFileDelete(elementId, env, rowIndex);
      }

      const updatedData = testDataState.map((item) => {
        if (item.id === elementId) {
          const newData = [...item[envKey]];
          newData.splice(rowIndex, 1);
          if (newData.length === 0) {
            newData.push({ _name: '', _tags: '' });
          }
          return { ...item, [envKey]: newData };
        }
        return item;
      });
      setTestDataState(updatedData);
      validateFields(elementId, envKey, updatedData);
    };

    const handleColumnTitleChange = (elementId, env, oldKey, newKey) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const updatedData = testDataState.map((item) => {
        if (item.id === elementId) {
          const newData = item[envKey].map((row) => {
            const updatedRow = { ...row };
            if (oldKey !== newKey) {
              updatedRow[newKey] = updatedRow[oldKey];
              delete updatedRow[oldKey];
            }
            return updatedRow;
          });
          const updatedColumnsOrder = item.columnsOrder[envKey].map((key) =>
            key === oldKey ? newKey : key,
          );
          return {
            ...item,
            [envKey]: newData,
            columnsOrder: {
              ...item.columnsOrder,
              [envKey]: updatedColumnsOrder,
            },
          };
        }
        return item;
      });
      setColumnBeingEdited({
        key: '',
        elementId: '',
      });
      setTestDataState(updatedData);
    };

    const handleTitleInputChange = (elementId, env, oldKey, newKey) => {
      setInputValue(newKey);
      const envKey = `testdata_${env.toLowerCase()}`;
      const lowerCaseNewKey = newKey.toLowerCase();
      const lowerCaseColumnsOrder = testDataState
        .find((item) => item.id === elementId)
        .columnsOrder[envKey].map((key) => key.toLowerCase());

      const keyRegex = /^[a-zA-Z0-9_-]+$/;
      const hasInvalidKeyChars = newKey.length > 0 && !keyRegex.test(newKey);
      const startsWithNumeric = newKey.length > 0 && /^\d/.test(newKey);
      const isDuplicateOrReserved =
        [...lowerCaseColumnsOrder, 'name', 'tags', '_name', '_tags'].includes(
          lowerCaseNewKey,
        ) && oldKey.toLowerCase() !== lowerCaseNewKey;

      if (hasInvalidKeyChars) {
        setColumnTitleErrors((prevErrors) => ({
          ...prevErrors,
          [elementId]: {
            ...prevErrors[elementId],
            [envKey]: 'Invalid characters in key',
          },
        }));
      } else if (startsWithNumeric) {
        setColumnTitleErrors((prevErrors) => ({
          ...prevErrors,
          [elementId]: {
            ...prevErrors[elementId],
            [envKey]: 'Key cannot start with a number',
          },
        }));
      } else if (isDuplicateOrReserved) {
        setColumnTitleErrors((prevErrors) => ({
          ...prevErrors,
          [elementId]: {
            ...prevErrors[elementId],
            [envKey]: 'Column name already exists',
          },
        }));
      } else {
        setColumnTitleErrors((prevErrors) => {
          const { [envKey]: removed, ...rest } = prevErrors[elementId] || {};
          const cleanedErrors = {
            ...prevErrors,
            [elementId]: rest,
          };
          if (Object.keys(cleanedErrors[elementId]).length === 0) {
            delete cleanedErrors[elementId];
          }
          return cleanedErrors;
        });
      }
    };

    const handleUndoEditColumnTitle = (elementId, envKey) => {
      setInputValue(previousColumnTitle);
      setTimeout(() => {
        setColumnBeingEdited({ key: '', elementId: '' });
      }, 0);
      setPreviousColumnTitle('');
      if (
        columnTitleErrors[elementId] &&
        Object.keys(columnTitleErrors[elementId]).length > 0
      ) {
        setColumnTitleErrors((prevErrors) => {
          const { [envKey]: removed, ...rest } = prevErrors[elementId];
          const cleanedErrors = {
            ...prevErrors,
            [elementId]: rest,
          };
          if (Object.keys(cleanedErrors[elementId]).length === 0) {
            delete cleanedErrors[elementId];
          }
          return cleanedErrors;
        });
      }
    };

    /**
     * Creates a keydown handler for column title editing
     * @param {string} elementId - The element ID
     * @param {string} env - The environment name
     * @param {string} key - The current column key
     * @param {string} envKey - The environment key (testdata_${env})
     * @returns {Function} The keydown event handler
     */
    const createColumnTitleKeyDownHandler = (elementId, env, key, envKey) => {
      return (event) => {
        const currentInputValue =
          (inputRef.current &&
            inputRef.current.inputRef &&
            inputRef.current.inputRef.value) ||
          inputValue ||
          key;

        if (event.key === 'Enter') {
          event.preventDefault();
          if (
            (!columnTitleErrors[elementId] ||
              !columnTitleErrors[elementId][envKey]) &&
            currentInputValue !== key
          ) {
            handleColumnTitleChange(elementId, env, key, currentInputValue);
          } else if (
            !columnTitleErrors[elementId] ||
            !columnTitleErrors[elementId][envKey]
          ) {
            setTimeout(() => {
              setColumnBeingEdited({ key: '', elementId: '' });
            }, 0);
          }
        } else if (event.key === 'Tab') {
          if (
            (!columnTitleErrors[elementId] ||
              !columnTitleErrors[elementId][envKey]) &&
            currentInputValue !== key
          ) {
            event.preventDefault();
            handleColumnTitleChange(elementId, env, key, currentInputValue);
          } else if (
            !columnTitleErrors[elementId] ||
            !columnTitleErrors[elementId][envKey]
          ) {
            setTimeout(() => {
              setColumnBeingEdited({ key: '', elementId: '' });
            }, 0);
          } else {
            event.preventDefault();
          }
        } else if (event.key === 'Escape') {
          event.preventDefault();
          handleUndoEditColumnTitle(elementId, envKey);
        }
      };
    };

    const getNextColumnNumber = (columns) => {
      const columnNumbers = columns
        .filter((key) => key.startsWith('column'))
        .map((key) => parseInt(key.replace('column', ''), 10))
        .sort((a, b) => a - b);

      let nextNumber = 1;
      for (const number of columnNumbers) {
        if (number !== nextNumber) {
          break;
        }
        nextNumber++;
      }
      return nextNumber;
    };

    const handleAddColumn = (env, testCaseId) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const updatedData = testDataState.map((testCase) => {
        if (testCase.id !== testCaseId) {
          return testCase;
        }

        const nextColumnNumber = getNextColumnNumber(
          testCase.columnsOrder[envKey],
        );
        const newColumnKey = `column${nextColumnNumber}`;

        const newData = testCase[envKey].map((row) => ({
          ...row,
          [newColumnKey]: '',
        }));

        return {
          ...testCase,
          [envKey]: newData,
          columnsOrder: {
            ...testCase.columnsOrder,
            [envKey]: [...testCase.columnsOrder[envKey], newColumnKey],
          },
        };
      });
      setTestDataState(updatedData);
      validateFields(testCaseId, envKey, updatedData);
    };

    const handleRemoveColumn = (key, testCaseId) => {
      const envKey = `testdata_${currentEnv.toLowerCase()}`;
      const updatedData = testDataState.map((testCase) => {
        if (testCase.id !== testCaseId) {
          return testCase;
        }
        const newData = {
          ...testCase,
          [envKey]: testCase[envKey].map((row) => {
            const { [key]: removed, ...rest } = row;
            return rest;
          }),
        };
        const newColumnsOrder = {
          ...testCase.columnsOrder,
          [envKey]: testCase.columnsOrder[envKey].filter(
            (columnKey) => columnKey !== key,
          ),
        };
        return {
          ...testCase,
          ...newData,
          columnsOrder: newColumnsOrder,
        };
      });

      setColumnTitleErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        Object.keys(newErrors).forEach((elementId) => {
          if (
            newErrors[elementId] &&
            newErrors[elementId][envKey] === 'Column name already exists'
          ) {
            delete newErrors[elementId][envKey];
            if (Object.keys(newErrors[elementId]).length === 0) {
              delete newErrors[elementId];
            }
          }
        });
        return newErrors;
      });

      setTestDataState(updatedData);
    };

    /**
     * Handle bulk add field - adds a field with specified value to jobs
     * @param {string} fieldKey - The field key to add
     * @param {string} fieldValue - The value for the field
     * @param {string} environment - The current environment
     * @param {boolean} replaceExisting - Whether to replace values in jobs that already have the field
     */
    const handleBulkAddField = (
      fieldKey,
      fieldValue,
      environment,
      replaceExisting = false,
    ) => {
      const envKey = `testdata_${environment.toLowerCase()}`;

      const addedToJobs = [];
      const replacedJobs = [];
      const skippedJobs = [];
      const failedJobs = [];

      const updatedData = testDataState.map((testCase) => {
        try {
          const fieldExists =
            testCase.columnsOrder &&
            testCase.columnsOrder[envKey] &&
            testCase.columnsOrder[envKey].includes(fieldKey);

          if (fieldExists) {
            if (replaceExisting) {
              const updatedData = testCase[envKey].map((row) => ({
                ...row,
                [fieldKey]: fieldValue,
              }));

              replacedJobs.push(testCase.displayname || testCase.id);

              return {
                ...testCase,
                [envKey]: updatedData,
              };
            }

            skippedJobs.push(testCase.displayname || testCase.id);
            return testCase;
          }

          const newData = testCase[envKey].map((row) => ({
            ...row,
            [fieldKey]: fieldValue,
          }));

          const newColumnsOrder = {
            ...testCase.columnsOrder,
            [envKey]: [...(testCase.columnsOrder[envKey] || []), fieldKey],
          };

          addedToJobs.push(testCase.displayname || testCase.id);

          return {
            ...testCase,
            [envKey]: newData,
            columnsOrder: newColumnsOrder,
          };
        } catch (error) {
          failedJobs.push(testCase.displayname || testCase.id);
          return testCase;
        }
      });

      setTestDataState(updatedData);

      updatedData.forEach((testCase) => {
        const jobName = testCase.displayname || testCase.id;
        if (addedToJobs.includes(jobName) || replacedJobs.includes(jobName)) {
          validateFields(testCase.id, envKey, updatedData);
        }
      });

      if (failedJobs.length > 0) {
        const successCount = addedToJobs.length + replacedJobs.length;
        throw new Error(
          `Field updated in ${successCount} job(s). Failed for ${
            failedJobs.length
          } job(s). ${
            skippedJobs.length > 0
              ? `Skipped ${skippedJobs.length} job(s) that already had this field.`
              : ''
          }`,
        );
      }

      if (
        addedToJobs.length === 0 &&
        replacedJobs.length === 0 &&
        skippedJobs.length > 0
      ) {
        throw new Error(
          `Field '${fieldKey}' already exists in all ${skippedJobs.length} selected job(s). No changes made. Enable "Replace existing" to update values.`,
        );
      }

      const messages = [];
      if (replacedJobs.length > 0) {
        messages.push(`updated in ${replacedJobs.length} job(s)`);
      }
      if (addedToJobs.length > 0) {
        messages.push(`added to ${addedToJobs.length} job(s)`);
      }

      const successMessage = `Field '${fieldKey}' with value '${fieldValue}' ${messages.join(
        ' and ',
      )} successfully`;

      if (skippedJobs.length > 0) {
        throw new Error(
          `${successMessage}. ${skippedJobs.length} job(s) already had this field and were skipped.`,
        );
      }
    };

    // Handle iteration file upload
    const handleIterationFileChange = (event, testCaseId, env) => {
      const file = event.target.files[0];
      const envKey = `testdata_${env.toLowerCase()}`;
      setIterationFiles((prevFiles) => {
        const updatedFiles = { ...prevFiles };
        if (!updatedFiles[testCaseId]) {
          updatedFiles[testCaseId] = {};
        }
        updatedFiles[testCaseId][envKey] = file;
        return updatedFiles;
      });
    };

    // Handle test data file upload
    const handleTestDataFileChange = (event, elementId, env, rowIndex) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      const envKey = `testdata_${env.toLowerCase()}`;
      const updatedData = testDataState.map((item) => {
        if (item.id === elementId) {
          const filename = file.name;

          if (filename.includes(' ')) {
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              [elementId]: {
                ...prevErrors[elementId],
                [envKey]: {
                  ...prevErrors[elementId]?.[envKey],
                  [rowIndex]: {
                    ...prevErrors[elementId]?.[envKey]?.[rowIndex],
                    invalidFilename: 'No spaces allowed in filename',
                  },
                },
              },
            }));
            const newData = [...item[envKey]];
            newData[rowIndex] = {
              ...newData[rowIndex],
              testdatafile: '',
            };
            return {
              ...item,
              [envKey]: newData,
            };
          }
          if (filename.length > MAX_FILENAME_LENGTH) {
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              [elementId]: {
                ...prevErrors[elementId],
                [envKey]: {
                  ...prevErrors[elementId]?.[envKey],
                  [rowIndex]: {
                    ...prevErrors[elementId]?.[envKey]?.[rowIndex],
                    invalidFilename: `Max ${MAX_FILENAME_LENGTH} chars allowed in filename`,
                  },
                },
              },
            }));
            const newData = [...item[envKey]];
            newData[rowIndex] = {
              ...newData[rowIndex],
              testdatafile: '',
            };
            return {
              ...item,
              [envKey]: newData,
            };
          }

          // Clear filename errors if valid
          setValidationErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (
              updatedErrors[elementId]?.[envKey]?.[rowIndex]?.invalidFilename
            ) {
              delete updatedErrors[elementId][envKey][rowIndex].invalidFilename;
              if (
                Object.keys(updatedErrors[elementId][envKey][rowIndex])
                  .length === 0
              ) {
                delete updatedErrors[elementId][envKey][rowIndex];
              }
              if (Object.keys(updatedErrors[elementId][envKey]).length === 0) {
                delete updatedErrors[elementId][envKey];
              }
              if (Object.keys(updatedErrors[elementId]).length === 0) {
                delete updatedErrors[elementId];
              }
            }
            return updatedErrors;
          });

          const newData = [...item[envKey]].map((it) => ({
            ...it,
            testdatafile: !it.testdatafile ? '' : it.testdatafile,
          }));
          newData[rowIndex].testdatafile = file;
          const updatedColumnsOrder = item.columnsOrder[envKey].includes(
            'testdatafile',
          )
            ? item.columnsOrder[envKey]
            : [...item.columnsOrder[envKey], 'testdatafile'];

          item[`run_as_single_transaction_${env.toLowerCase()}`] = false;

          const newRunAsSingleTransaction = { ...runAsSingleTransaction };
          newRunAsSingleTransaction[elementId] =
            newRunAsSingleTransaction[elementId] || {};
          newRunAsSingleTransaction[elementId][env] = false;
          setRunAsSingleTransaction((prevState) => ({
            ...prevState,
            ...newRunAsSingleTransaction,
          }));

          return {
            ...item,
            [envKey]: newData,
            columnsOrder: {
              ...item.columnsOrder,
              [envKey]: updatedColumnsOrder,
            },
          };
        }
        return item;
      });
      setTestDataState(updatedData);
    };

    const createPayload = (currentState) => {
      const payload = currentState.reduce((acc, testCase) => {
        let runAsSingleTransactionEnv = {};
        if (buildTool === BUILD_TOOLS.PLAYWRIGHT) {
          runAsSingleTransactionEnv = Object.keys(
            runAsSingleTransaction[testCase.id] || {},
          ).reduce((envAcc, env) => {
            envAcc[`run_as_single_transaction_${env.toLowerCase()}`] =
              runAsSingleTransaction[testCase.id][env];
            return envAcc;
          }, {});
        }

        acc[testCase.id] = {
          displayname: testCase.displayname,
          testcasename: testCase.testcasename,
          description: testCase.description,
          os: testCase.os,
          testcasedir: testCase.testcasedir,
          parameters: testCase.parameters,
          notsupportedenv: testCase.notsupportedenv,
          wiki: testCase.wiki,
          lineitems: testCase.lineitems,
          ...transformTestData(testCase),
          ...runAsSingleTransactionEnv,
        };
        return acc;
      }, {});
      return payload;
    };

    const handleClearFile = (elementId, env, rowIndex) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const updatedData = testDataState.map((item) => {
        if (item.id === elementId) {
          const newData = [...item[envKey]];
          newData[rowIndex].testdatafile = '';
          return { ...item, [envKey]: newData };
        }
        return item;
      });
      setTestDataState(updatedData);
    };

    const handleClearIterationFile = (testCaseId, env) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      setIterationFiles((prevFiles) => {
        const updatedFiles = { ...prevFiles };
        if (!updatedFiles[testCaseId]) {
          updatedFiles[testCaseId] = {};
        }
        updatedFiles[testCaseId][envKey] = null;
        return updatedFiles;
      });
    };

    const renderFileCell = (rowData, key, testCaseId, env, rowIndex) => {
      if (rowData[key]) {
        const fileName =
          rowData[key] instanceof File
            ? rowData[key].name
            : rowData[key].split('/').pop();
        if (rowData[key] instanceof File) {
          return (
            <>
              <span
                title={fileName}
                style={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fileName}
              </span>
              <IconButton
                onClick={() => handleClearFile(testCaseId, env, rowIndex)}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          );
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <a
              href={rowData[key]}
              download
              title={fileName}
              onClick={(e) => {
                e.preventDefault();
                downloadFile.fromURL(e.target.href);
              }}
              style={{
                display: 'inline-block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fileName}
            </a>
            <IconButton
              onClick={() => handleFileDelete(testCaseId, env, rowIndex)}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span className="text-muted">No file uploaded</span>
          {validationErrors[testCaseId]?.[`testdata_${env.toLowerCase()}`]?.[
            rowIndex
          ]?.invalidFilename && (
            <>
              <div className="is-invalid"></div>
              <div className="invalid-feedback">
                {
                  validationErrors[testCaseId][`testdata_${env.toLowerCase()}`][
                    rowIndex
                  ].invalidFilename
                }
              </div>
            </>
          )}
        </div>
      );
    };

    const renderIterationFileLabel = (testCaseId, env) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const file = iterationFiles[testCaseId]?.[envKey];

      const fileName = file
        ? file instanceof File
          ? file.name
          : file.split('/').pop()
        : null;

      if (!file) {
        return <span className="text-muted">No file uploaded</span>;
      }

      if (file instanceof File) {
        return (
          <>
            <span>{fileName}</span>
            <IconButton
              onClick={() => {
                handleClearIterationFile(testCaseId, env);
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        );
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <a
            href={file}
            download
            onClick={(e) => {
              e.preventDefault();
              downloadFile.fromURL(e.target.href);
            }}
          >
            {fileName}
          </a>
          <IconButton
            onClick={() => {
              handleClearIterationFile(testCaseId, env);
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      );
    };

    const renderTestIterationFileSection = (testCaseId, env) => {
      return (
        <div>
          <div className="test-data-file-section">
            <section className="file-upload-section">
              <label
                htmlFor={`fileUpload-${testCaseId}-${env}`}
                className="btn btn-primary choose-file-button"
              >
                Iteration file
              </label>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                id={`fileUpload-${testCaseId}-${env}`}
                onChange={(e) => handleIterationFileChange(e, testCaseId, env)}
                style={{ display: 'none' }}
              />
            </section>{' '}
            {renderIterationFileLabel(testCaseId, env)}
          </div>
          <div className="warning-text">
            <small className="form-text text-muted">
              **Uploading a new file will overwrite the existing file for this
              test execution.
            </small>
          </div>
        </div>
      );
    };

    const selectControlHeight = compactTable
      ? 30
      : 'calc(1.5em + .75rem + 2px)';
    const selectFontSize = compactTable ? '0.875rem' : undefined;
    const selectTextColor = '#111';
    const selectBgColor = '#fff';
    const selectHoverBgColor = '#f2f4f8';
    const SelectSingleValue = (props) => {
      const title =
        props?.data?.label || props?.data?.value || props?.children || '';
      return (
        <selectComponents.SingleValue {...props} title={String(title)}>
          {props.children}
        </selectComponents.SingleValue>
      );
    };
    const customSelectStyles = {
      control: (provided) => ({
        ...provided,
        minHeight: selectControlHeight,
        height: selectControlHeight,
        fontSize: selectFontSize,
        color: selectTextColor,
        backgroundColor: selectBgColor,
      }),
      container: (provided) => ({
        ...provided,
        width: '100%',
        minWidth: 0,
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: selectControlHeight,
        padding: '0 8px',
        overflow: 'hidden',
      }),
      input: (provided) => ({
        ...provided,
        margin: '0',
        padding: '0',
        fontSize: selectFontSize,
      }),
      singleValue: (provided) => ({
        ...provided,
        fontSize: selectFontSize,
        color: selectTextColor,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }),
      placeholder: (provided) => ({
        ...provided,
        fontSize: selectFontSize,
        color: selectTextColor,
      }),
      option: (provided, state) => ({
        ...provided,
        fontSize: selectFontSize,
        color: selectTextColor,
        backgroundColor: state.isSelected
          ? '#e5e7eb'
          : state.isFocused
            ? selectHoverBgColor
            : selectBgColor,
        fontWeight: state.isSelected ? 600 : 400,
        ':active': {
          backgroundColor: '#d1d5db',
        },
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: selectBgColor,
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: selectControlHeight,
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 999999,
      }),
    };

    const getColumnWidthCh = (
      columnKey,
      rows,
      min = 10,
      max = 26,
      extra = [],
    ) => {
      const values = (rows || []).map((row) => {
        const value = row?.[columnKey];
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') {
          if (typeof value.value === 'string') return value.value;
          if (typeof value.label === 'string') return value.label;
          return '';
        }
        return String(value);
      });

      const extraValues = (extra || []).map((value) =>
        value === null || value === undefined ? '' : String(value),
      );
      const maxLen = Math.max(
        String(columnKey || '').length,
        ...values.map((value) => value.length),
        ...extraValues.map((value) => value.length),
      );
      return Math.min(max, Math.max(min, maxLen));
    };

    const getSelectStylesWithError = (hasError = false) => ({
      ...customSelectStyles,
      control: (provided, state) => ({
        ...customSelectStyles.control(provided, state),
        ...(hasError && {
          borderColor: '#dc3545',
          boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
        }),
      }),
    });

    const getColumnTitleSelectStyles = (minWidthCh, hasError = false) => ({
      ...customSelectStyles,
      control: (provided, state) => ({
        ...customSelectStyles.control(provided, state),
        ...(hasError && {
          borderColor: '#dc3545',
          boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
        }),
      }),
      container: (provided) => ({
        ...provided,
        overflow: 'visible',
        minWidth: `${minWidthCh}ch`,
      }),
    });

    const copyDataFromExistingEnv = (elementId, toEnv) => {
      const toEnvKey = `testdata_${toEnv.toLowerCase()}`;
      let dataCopied = false;
      const updatedData = testDataState.map((item) => {
        if (item.id === elementId) {
          const fromEnvKey = Object.keys(item).find(
            (key) =>
              key.startsWith('testdata_') &&
              key !== toEnvKey &&
              !hasOnlyNameAndTags(item, key),
          );

          if (fromEnvKey) {
            dataCopied = true;
            return {
              ...item,
              columnsOrder: {
                ...item.columnsOrder,
                [toEnvKey]: item.columnsOrder[fromEnvKey],
              },
              [toEnvKey]: item[fromEnvKey].map((row) => {
                const newRow = { ...row };
                delete newRow.tableData;
                return newRow;
              }),
            };
          }
        }
        return item;
      });
      setTestDataState(updatedData);
      validateFields(elementId, toEnvKey, updatedData);

      if (!dataCopied) {
        handleAddColumn(toEnv, elementId);
      }
    };

    const renderTestDataSection = (element, env) => {
      const envKey = `testdata_${env.toLowerCase()}`;
      const currentColumnsCount = element.columnsOrder[envKey].length;
      const currentRowsCount = element[envKey].length;
      const rowsForSizing = element[envKey] || [];
      const denseCellStyle = compactTable
        ? {
            padding: '6px 8px',
            fontSize: '0.8125rem',
            minHeight: 52,
          }
        : { minHeight: 56 };
      const denseHeaderStyle = compactTable
        ? { padding: '6px 8px', fontSize: '0.875rem' }
        : {};
      const denseInputStyle = compactTable
        ? { height: 30, padding: '4px 8px', fontSize: '0.8125rem' }
        : undefined;
      const nameColumnWidth = getColumnWidthCh('_name', rowsForSizing, 12, 28);
      const tagsColumnWidth = getColumnWidthCh('_tags', rowsForSizing, 12, 28);

      const columns = [
        {
          title: 'Actions',
          field: 'actions',
          render: (rowData) => (
            <div>
              <ReactTooltip id="add-column" place="bottom" effect="solid">
                <span>Add field</span>
              </ReactTooltip>
              <IconButton
                onClick={() => handleAddColumn(env, element.id)}
                size="small"
                data-tip
                data-for="add-column"
                disabled={currentColumnsCount >= MAX_COLUMNS}
                className={
                  currentColumnsCount >= MAX_COLUMNS
                    ? 'icon-button-disabled'
                    : ''
                }
              >
                <ViewColumnIcon />
              </IconButton>
              <ReactTooltip id="add-row" place="bottom" effect="solid">
                <span>Add row</span>
              </ReactTooltip>
              <IconButton
                onClick={() => handleAddRow(element.id, env, rowData)}
                size="small"
                data-tip
                data-for="add-row"
                disabled={currentRowsCount >= MAX_ROWS}
                className={
                  currentRowsCount >= MAX_ROWS ? 'icon-button-disabled' : ''
                }
              >
                <AddBoxIcon />
              </IconButton>
              <ReactTooltip id="remove-row" place="bottom" effect="solid">
                <span>Remove row</span>
              </ReactTooltip>
              <IconButton
                onClick={() =>
                  handleRemoveRow(element.id, env, rowData.tableData.id)
                }
                size="small"
                data-tip
                data-for="remove-row"
              >
                <DeleteIcon />
              </IconButton>
              <ReactTooltip id="upload-file" place="bottom" effect="solid">
                <span>Upload file</span>
              </ReactTooltip>
              <IconButton
                onClick={() =>
                  document
                    .getElementById(
                      `fileUpload-${element.id}-${env}-${rowData.tableData.id}`,
                    )
                    .click()
                }
                size="small"
                data-tip
                data-for="upload-file"
              >
                <CloudUploadIcon />
              </IconButton>
              <input
                type="file"
                id={`fileUpload-${element.id}-${env}-${rowData.tableData.id}`}
                onChange={(e) =>
                  handleTestDataFileChange(
                    e,
                    element.id,
                    env,
                    rowData.tableData.id,
                  )
                }
                style={{ display: 'none' }}
              />
            </div>
          ),
          cellStyle: {
            minWidth: '150px',
            ...denseCellStyle,
            position: 'sticky',
            left: 0,
            zIndex: 1,
            background: '#fff',
            boxShadow: '2px 0 0 rgba(0,0,0,0.06)',
            borderRight: '1px solid rgba(0,0,0,0.08)',
          },
          headerStyle: {
            minWidth: '150px',
            fontSize: compactTable ? '0.875rem' : '1rem',
            fontWeight: 600,
            ...denseHeaderStyle,
            position: 'sticky',
            left: 0,
            zIndex: 2,
            background: '#fff',
            boxShadow: '2px 0 0 rgba(0,0,0,0.06)',
            borderRight: '1px solid rgba(0,0,0,0.08)',
          },
        },
        {
          title: 'name',
          field: '_name',
          editable: 'never',
          cellStyle: {
            maxWidth: `${nameColumnWidth}ch`,
            minWidth: `${nameColumnWidth}ch`,
            ...denseCellStyle,
          },
          headerStyle: {
            maxWidth: `${nameColumnWidth}ch`,
            minWidth: `${nameColumnWidth}ch`,
            fontSize: compactTable ? '0.875rem' : '1rem',
            fontWeight: 600,
            ...denseHeaderStyle,
          },
          render: (rowData) => (
            <DebouncedInputField
              elementId={element.id}
              env={env}
              rowIndex={rowData.tableData.id}
              fieldKey="_name"
              initialValue={rowData._name}
              onUpdate={(value) => {
                handleInputChange(
                  element.id,
                  env,
                  '_name',
                  value,
                  rowData.tableData.id,
                );
              }}
              validationErrors={validationErrors}
              envKey={envKey}
              showValidation={true}
              inputStyle={denseInputStyle}
            />
          ),
        },
        {
          title: 'tags',
          field: '_tags',
          editable: 'never',
          cellStyle: {
            maxWidth: `${tagsColumnWidth}ch`,
            minWidth: `${tagsColumnWidth}ch`,
            ...denseCellStyle,
          },
          headerStyle: {
            maxWidth: `${tagsColumnWidth}ch`,
            minWidth: `${tagsColumnWidth}ch`,
            fontSize: compactTable ? '0.875rem' : '1rem',
            fontWeight: 600,
            ...denseHeaderStyle,
          },
          render: (rowData) => (
            <DebouncedInputField
              elementId={element.id}
              env={env}
              rowIndex={rowData.tableData.id}
              fieldKey="_tags"
              initialValue={rowData._tags}
              onUpdate={(value) => {
                handleInputChange(
                  element.id,
                  env,
                  '_tags',
                  value,
                  rowData.tableData.id,
                );
              }}
              validationErrors={validationErrors}
              envKey={envKey}
              showValidation={true}
              inputStyle={denseInputStyle}
            />
          ),
        },
        ...element.columnsOrder[envKey]
          .filter((key) => key !== '_name' && key !== '_tags')
          .filter((key) => key !== 'testiterationfile')
          .map((key) => {
            const field = (globalFields || []).find((f) => f.key === key);
            const optionValues = field
              ? [
                  ...(field.options || []).map(
                    (opt) => opt?.value || opt?.label || '',
                  ),
                  field.value,
                  field.default,
                ].filter(Boolean)
              : [];
            const columnWidth = getColumnWidthCh(
              key,
              rowsForSizing,
              globalFieldsKeys.includes(key) ? 12 : 10,
              26,
              optionValues,
            );
            const columnTitleError =
              columnTitleErrors[element.id]?.[envKey];
            return {
              title: (
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {columnBeingEdited.key === key &&
                    columnBeingEdited.elementId === element.id ? (
                      <Select
                        value={
                          key
                            ? {
                                value: key,
                                label: key,
                              }
                            : null
                        }
                        options={globalFieldsKeys
                          .filter(
                            (fieldKey) =>
                              !element.columnsOrder[envKey].includes(fieldKey),
                          )
                          .map((fieldKey) => ({
                            value: fieldKey,
                            label: fieldKey,
                          }))}
                        inputValue={inputValue}
                        ref={inputRef}
                        onChange={(option) => {
                          if (!option) {
                            return;
                          }
                          handleColumnTitleChange(
                            element.id,
                            env,
                            key,
                            option.value,
                          );
                        }}
                        onInputChange={(nextInputValue, { action }) => {
                          if (action === 'input-change') {
                            setInputValue(nextInputValue);
                            handleTitleInputChange(
                              element.id,
                              env,
                              key,
                              nextInputValue,
                            );
                          }
                        }}
                        onMenuOpen={() => {
                          setMenuIsOpen(true);
                        }}
                        onMenuClose={() => {
                          setMenuIsOpen(false);
                        }}
                        menuIsOpen={menuIsOpen}
                        onBlur={(event) => {
                          if (!event.target.value) {
                            return;
                          }
                          if (!globalFieldsKeys.includes(event.target.value)) {
                            if (
                              !columnTitleErrors[element.id] ||
                              !columnTitleErrors[element.id][envKey]
                            ) {
                              handleColumnTitleChange(
                                element.id,
                                env,
                                key,
                                event.target.value,
                              );
                            }
                          }
                        }}
                        onKeyDown={createColumnTitleKeyDownHandler(
                          element.id,
                          env,
                          key,
                          envKey,
                        )}
                        filterOption={() => true}
                        styles={getColumnTitleSelectStyles(
                          columnWidth,
                          !!columnTitleError,
                        )}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        autoFocus
                        noOptionsMessage={() => null}
                      />
                    ) : (
                      <span className="manage-test-data-column-title">{key}</span>
                    )}
                    {columnBeingEdited.key === key &&
                      columnBeingEdited.elementId === element.id &&
                      columnTitleError && (
                      <div
                        className="invalid-feedback d-block"
                        style={{
                          textSizeAdjust: '100%',
                          fontFamily:
                            'yArtifaktElement-Light, Arial, Helvetica, sans-serif',
                          fontSize: '9.1px',
                          fontWeight: 400,
                          letterSpacing: '0.121826px',
                          lineHeight: '13.013px',
                          textAlign: 'left',
                          textIndent: 0,
                        }}
                      >
                        {columnTitleError}
                      </div>
                    )}
                  </div>
                  {columnBeingEdited.key === key &&
                  columnBeingEdited.elementId === element.id ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '5px',
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          handleColumnTitleChange(
                            element.id,
                            env,
                            key,
                            inputValue || key,
                          );
                        }}
                        size="small"
                        disabled={!!columnTitleError}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          handleUndoEditColumnTitle(element.id, envKey)
                        }
                        size="small"
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          handleRemoveColumn(key, element.id);
                          setColumnBeingEdited({
                            key: '',
                            elementId: '',
                          });
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ) : (
                    key !== 'testdatafile' && (
                      <IconButton
                        onClick={() => {
                          setInputValue(key);
                          setPreviousColumnTitle(key);
                          setColumnBeingEdited({
                            key: key,
                            elementId: element.id,
                          });
                        }}
                        size="small"
                        style={{ marginLeft: '5px' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )
                  )}
                </div>
              ),
              field: key,
              editable: key === 'testdatafile' ? 'never' : 'always',
              render: (rowData) => {
                const rowIndex = rowData.tableData.id;
                if (globalFieldsKeys.includes(key)) {
                  const field = (globalFields || []).find((f) => f.key === key);
                  if (!field) return null;

                  const isStringType = field.type === 'string';
                  const currentValue =
                    rowData[key] || (isStringType ? field.value : '');

                  const options = isStringType
                    ? [{ value: field.value, label: field.value }]
                    : (field.options || []).map((opt) => ({
                        value: opt.value,
                        label: opt.value,
                      }));

                  const cellInvalidValue =
                    validationErrors[element.id]?.[envKey]?.[rowIndex]
                      ?.invalidValueByColumn?.[key];

                  return (
                    <div>
                      <Select
                        value={
                          currentValue
                            ? {
                                value: currentValue,
                                label: currentValue,
                              }
                            : null
                        }
                        onChange={(option) => {
                          handleInputChange(
                            element.id,
                            env,
                            key,
                            option ? option.value : '',
                            rowIndex,
                          );
                        }}
                        options={options}
                        components={{ SingleValue: SelectSingleValue }}
                        onInputChange={(nextValue, { action }) => {
                          if (action === 'input-change') {
                            if (isStringType) {
                              handleInputChange(
                                element.id,
                                env,
                                key,
                                nextValue,
                                rowIndex,
                              );
                            } else if (
                              field.options &&
                              !field.options.some(
                                (opt) => opt.value === nextValue,
                              )
                            ) {
                              handleInputChange(
                                element.id,
                                env,
                                key,
                                nextValue,
                                rowIndex,
                              );
                            }
                          }
                        }}
                        onBlur={(event) => {
                          if (!event.target.value) {
                            return;
                          }
                          if (isStringType) {
                            handleInputChange(
                              element.id,
                              env,
                              key,
                              event.target.value,
                              rowIndex,
                            );
                          } else if (
                            field.options &&
                            !field.options.some(
                              (opt) => opt.value === event.target.value,
                            )
                          ) {
                            handleInputChange(
                              element.id,
                              env,
                              key,
                              event.target.value,
                              rowIndex,
                            );
                          }
                        }}
                        styles={getSelectStylesWithError(!!cellInvalidValue)}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {cellInvalidValue && (
                        <div
                          className="invalid-feedback d-block"
                        >
                          {cellInvalidValue}
                        </div>
                      )}
                    </div>
                  );
                }
                if (key === 'testdatafile') {
                  return renderFileCell(
                    rowData,
                    key,
                    element.id,
                    env,
                    rowIndex,
                  );
                }
                return (
                  <DebouncedInputField
                    elementId={element.id}
                    env={env}
                    rowIndex={rowIndex}
                    fieldKey={key}
                    initialValue={rowData[key]}
                    onUpdate={(value) => {
                      handleInputChange(element.id, env, key, value, rowIndex);
                    }}
                    validationErrors={validationErrors}
                    envKey={envKey}
                    showValidation={true}
                    inputStyle={denseInputStyle}
                  />
                );
              },
              cellStyle: {
                maxWidth: `${columnWidth}ch`,
                minWidth: `${columnWidth}ch`,
                ...denseCellStyle,
              },
              headerStyle: {
                maxWidth: `${columnWidth}ch`,
                minWidth: `${columnWidth}ch`,
                fontSize: compactTable ? '0.875rem' : '1rem',
                fontWeight: 600,
                ...denseHeaderStyle,
              },
            };
          }),
      ];

      return (
        <div>
          {buildTool === BUILD_TOOLS.MAVEN &&
            renderTestIterationFileSection(element.id, env)}
          {buildTool === BUILD_TOOLS.PLAYWRIGHT && (
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id={`runAsSingleTransaction-${element.id}-${env}`}
                checked={runAsSingleTransaction[element.id]?.[env] || false}
                onChange={(e) =>
                  handleRunAsSingleTransactionChange(
                    element.id,
                    env,
                    e.target.checked,
                  )
                }
                disabled={element[envKey].some((row) => row.testdatafile)}
              />
              <label
                className="form-check-label"
                htmlFor={`runAsSingleTransaction-${element.id}-${env}`}
              >
                Run as single transaction
              </label>
            </div>
          )}
          {hasOnlyNameAndTags(element, envKey) ? (
            <ManageTestDataEmptyState
              onAddFields={() => copyDataFromExistingEnv(element.id, env)}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  flexGrow: 1,
                  width: 0,
                  minWidth: 0,
                  overflowX: 'auto',
                }}
              >
                <MaterialTable
                  title={null}
                  columns={columns}
                  data={removeTableData(element[envKey])}
                  options={{
                    paging: false,
                    search: false,
                    actionsColumnIndex: 0,
                    sorting: false,
                    toolbar: false,
                    padding: compactTable ? 'dense' : 'default',
                    rowStyle: compactTable
                      ? { fontSize: '0.8125rem' }
                      : undefined,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      );
    };

    const transformTestData = (testCase) => {
      return Object.keys(testCase)
        .filter((envKey) => envKey.startsWith('testdata_'))
        .reduce((acc, envKey) => {
          acc[envKey] = testCase[envKey].map((item) => {
            const transformedItem = {
              _tags: item._tags ? item._tags.split(/[, ]+/) : [],
              ...Object.keys(item)
                .filter((key) => key !== '_tags')
                .reduce((obj, key) => {
                  obj[key] = item[key];
                  return obj;
                }, {}),
            };
            return transformedItem;
          });
          if (
            iterationFiles[testCase.id] &&
            iterationFiles[testCase.id][envKey] &&
            !(iterationFiles[testCase.id][envKey] instanceof File) &&
            iterationFiles[testCase.id][envKey].startsWith('http')
          ) {
            acc[envKey][acc[envKey].length - 1]['testiterationfile'] =
              iterationFiles[testCase.id][envKey];
          }
          return acc;
        }, {});
    };

    const extractUploadedFiles = (currentState) => {
      const uploadedFiles = {};

      currentState.forEach((testCase) => {
        const testCaseId = testCase.id;
        uploadedFiles[testCaseId] = {};

        Object.keys(testCase)
          .filter((envKey) => envKey.startsWith('testdata_'))
          .forEach((envKey) => {
            uploadedFiles[testCaseId][envKey] = {};

            testCase[envKey].forEach((row, rowIndex) => {
              uploadedFiles[testCaseId][envKey][rowIndex] = {
                testdatafile: row.testdatafile,
                _name: row._name,
              };
            });
            if (buildTool === BUILD_TOOLS.MAVEN) {
              uploadedFiles[testCaseId][envKey]['testiterationfile'] = {
                testiterationfile: iterationFiles[testCaseId][envKey],
              };
            }
          });
      });

      return uploadedFiles;
    };

    const submitTestData = async () => {
      const payload = createPayload(testDataState);
      const uploadedFiles = extractUploadedFiles(testDataState);

      setLoading(true);
      try {
        const response = await onSubmit(payload, uploadedFiles);
        dispatch(addNewNotification(response.data.message, 'success'));
        return response;
      } catch (error) {
        if (error.response) {
          let detail = error.response.data.detail;
          if (Array.isArray(detail)) {
            detail = detail.map((d) => `${d.testcasename}: ${d.message}`);
          }
          dispatch(addNewNotification(detail, 'danger'));
        }
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const hasValidationError = (envKey) => {
      return Object.values(validationErrors).some(
        (error) => error[envKey] && Object.keys(error[envKey]).length > 0,
      );
    };

    const submitEnabled =
      validationErrors &&
      Object.keys(validationErrors).length === 0 &&
      Object.keys(columnTitleErrors).length === 0;

    const currentEnvKey = `testdata_${(currentEnv || '').toLowerCase()}`;
    const isEmptyForCurrentEnv =
      currentEnvKey &&
      Array.isArray(testDataState) &&
      testDataState.length > 0 &&
      testDataState.every((tc) => hasOnlyNameAndTags(tc, currentEnvKey));

    useEffect(() => {
      onStateChange({
        submitEnabled,
        loading,
        showActions: !isEmptyForCurrentEnv,
      });
    }, [submitEnabled, loading, onStateChange, isEmptyForCurrentEnv]);

    useImperativeHandle(ref, () => ({
      submit: submitTestData,
    }));

    if (!parent || !envlist || !envKeys.length) {
      return null;
    }

    return (
      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <div className="manage-test-data-tabs-row">
          <Tabs
            value={currentEnv}
            onChange={(_, newValue) => setCurrentEnv(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              minHeight: 48,
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiTabs-indicator': {
                height: 2,
                backgroundColor: 'text.primary',
              },
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
              '& .MuiTabs-scroller': {
                overflowX: 'auto',
                overflowY: 'hidden',
              },
              '& .MuiTabs-flexContainer': {
                overflow: 'visible',
              },
            }}
          >
            {envKeys.map((env) => {
              const envKey = `testdata_${env.toLowerCase()}`;
              const hasError = hasValidationError(envKey);

              return (
                <Tab
                  key={env}
                  value={env}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{env}</span>
                      {hasError && (
                        <i
                          className="bi bi-exclamation-circle"
                          style={{ color: 'red' }}
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '16px',
                    lineHeight: '20px',
                    minHeight: 48,
                    px: 2,
                    py: 1.5,
                    color: hasError ? 'error.main' : 'text.secondary',
                    '&.Mui-selected': {
                      color: hasError ? 'error.main' : 'text.primary',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.38,
                    },
                  }}
                />
              );
            })}
          </Tabs>
          <a
            href="https://wiki.autodesk.com/display/DES/Manage+Test+Data"
            target="_blank"
            rel="noopener noreferrer"
            className="manage-test-data-help-link"
            title="Help"
            style={{ textDecoration: 'none' }}
          >
            <span role="img" aria-label="question mark">
              ❓
            </span>{' '}
            Help
          </a>
        </div>

        <div>
          {testDataState.length > 1 && (
            <Accordion
              defaultExpanded
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: '16px !important',
                '&::before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  py: 0,
                  minHeight: 32,
                  '&.Mui-expanded': {
                    minHeight: 32,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  },
                  '& .MuiAccordionSummary-content.Mui-expanded': {
                    my: 1,
                  },
                }}
              >
                <Typography variant="subtitle1" color="textPrimary">
                  Bulk add fields to {testDataState.length} jobs
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <BulkAddFieldSection
                  jobsData={testDataState}
                  currentEnvironment={currentEnv}
                  globalFields={globalFields}
                  onAddField={handleBulkAddField}
                  validateFieldKey={validateBulkFieldKey}
                  validateFieldValue={validateBulkFieldValue}
                  showTitle={false}
                />
              </AccordionDetails>
            </Accordion>
          )}
          {testDataState.length > 1 ? (
            testDataState.map((element) => (
              <CollapsibleContent key={element.id} title={element.displayname}>
                {() => renderTestDataSection(element, currentEnv)}
              </CollapsibleContent>
            ))
          ) : (
            <div
              style={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              {renderTestDataSection(testDataState[0], currentEnv)}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default ManageTestDataEditor;
